import { z } from "zod";
import { ApiError, ApiErrorCodeSchema } from "./api-errors.js";
import { AssetTypeSchema, JobStatusSchema, PipelineStageSchema } from "./enums.js";

// Manifest the frontend polls (`GET /jobs/:id`). Asset URLs are signed CDN/S3
// URLs (ADR-0007 amended); slots are optional because the worker fills them as
// the pipeline emits `asset_ready` events. camelCase per ADR-0012 wire convention.
export const JobAssets = z.object({
  mask: z.string().url().optional(),
  meshSilhouette: z.string().url().optional(),
  textureFront: z.string().url().optional(),
  textureBack: z.string().url().optional(),
  thumbnail: z.string().url().optional(),
});
export type JobAssets = z.infer<typeof JobAssets>;

export const JobManifest = z.object({
  jobId: z.guid(),
  projectId: z.guid(),
  status: JobStatusSchema,
  stage: PipelineStageSchema.nullable(),
  assets: JobAssets,
  error: ApiError.nullable(),
});
export type JobManifest = z.infer<typeof JobManifest>;

// Internal plane: `POST /internal/jobs/:id/claim`. Body identifies the worker
// for ops (`worker_id` logged on the row). The 200/409 outcome is the mutex
// per ADR-0028 Layer 1 — body is informational only.
export const JobClaimRequest = z.object({
  worker_id: z.string().min(1),
});
export type JobClaimRequest = z.infer<typeof JobClaimRequest>;

export const JobClaimResponse = z.object({
  jobId: z.guid(),
  projectId: z.guid(),
  upload: z.object({
    bucket: z.string(),
    key: z.string(),
    mimeType: z.string(),
  }),
  projectKeyPrefix: z.string(),
});
export type JobClaimResponse = z.infer<typeof JobClaimResponse>;

// Per-event payload shapes shared between the wire schema (`JobEventPayload`)
// and the persisted POST body (`JobEventPostBody`). Defining them once keeps
// the two discriminated unions from drifting when a payload field is added.
const stageStartedPayload = z.object({ stage: PipelineStageSchema });
const assetReadyPayload = z.object({
  asset_type: AssetTypeSchema,
  s3_key: z.string().min(1),
  size_bytes: z.number().int().positive(),
});
const stageFailedPayload = z.object({
  stage: PipelineStageSchema,
  error_code: ApiErrorCodeSchema,
  // The detected subject (RT-DETR/COCO label, e.g. "cat"/"dog") so the frontend
  // can name the pet in the rejection copy. Only set for validation rejections
  // (ADR-0047); a coarse hint, never rendered raw — the frontend maps it to a
  // localized noun and falls back to a generic one for anything unrecognized.
  subject_species: z.string().optional(),
});

// Worker → API event payload, snake_case because it's the worker's wire
// (ADR-0002 amended). Three variants drive: liveness copy (`stage_started`),
// concrete renders (`asset_ready`), error UX (`stage_failed`).
export const JobEventPayload = z.discriminatedUnion("event_type", [
  z.object({ event_type: z.literal("stage_started"), payload: stageStartedPayload }),
  z.object({ event_type: z.literal("asset_ready"), payload: assetReadyPayload }),
  z.object({ event_type: z.literal("stage_failed"), payload: stageFailedPayload }),
]);
export type JobEventPayload = z.infer<typeof JobEventPayload>;

// What the worker actually POSTs to `/internal/jobs/:id/events`. `event_id` is
// the worker-generated dedup key (ADR-0028 §"`job_events` deduplication"); the
// API persists with `INSERT … ON CONFLICT (event_id) DO NOTHING`.
export const JobEventPostBody = z.discriminatedUnion("event_type", [
  z.object({
    event_id: z.guid(),
    event_type: z.literal("stage_started"),
    payload: stageStartedPayload,
  }),
  z.object({
    event_id: z.guid(),
    event_type: z.literal("asset_ready"),
    payload: assetReadyPayload,
  }),
  z.object({
    event_id: z.guid(),
    event_type: z.literal("stage_failed"),
    payload: stageFailedPayload,
  }),
]);
export type JobEventPostBody = z.infer<typeof JobEventPostBody>;

export const JobCompleteRequest = z.object({});
export type JobCompleteRequest = z.infer<typeof JobCompleteRequest>;
